/**
 * LO QUE SE VIENE: los partidos de mis clubes que todavía no se jugaron.
 *
 * QUÉ PROBLEMA RESUELVE. Un partido de cuadro no tiene fecha hasta que los
 * dos se ponen de acuerdo, y esa conversación no empieza si ninguno se entera
 * de que le tocó. Esta es la lista que lo dice, ordenada por cuándo se juega.
 *
 * YA NO DIBUJA LA ACTIVIDAD. Antes este componente servía las dos pestañas
 * con un `modo`, y las dos listas se pedían juntas. Al ganar la Actividad sus
 * filtros y sus páginas del servidor, las dos dejaron de parecerse: una es
 * una agenda corta que se lee entera, la otra es un historial de miles con
 * seis filtros. Un solo componente para las dos tendría la mitad de sus
 * estados apagados en cada pestaña. Ver `ActivitySection`.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MatchComment, MatchScore, WallEntry } from '@tennisup/contracts';

import { Alert } from './Alert';
import { Paginador, usePaginado } from './Paginado';
import { ScoreEditor, sidesOfMatch } from './ScoreEditor';
import { WallCard } from './WallCard';
import { useAuth } from '../auth/AuthContext';
import { api } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';

export function WallSection(): JSX.Element {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [proximos, setProximos] = useState<WallEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anotando, setAnotando] = useState<WallEntry | null>(null);

  const load = useCallback(async () => {
    try {
      setProximos(await api.wallUpcoming());
    } catch (caught) {
      setError(errorMessage(t, caught));
      // Lista vacía y no nula: sin esto la pantalla se queda en «Cargando…»
      // para siempre con el error arriba, y no se entiende cuál de las dos
      // cosas está pasando.
      setProximos([]);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const pagina = usePaginado(proximos ?? []);

  /**
   * DESPUÉS DE ANOTAR SE VUELVE A PEDIR TODO.
   *
   * Anotar un resultado saca un partido de esta lista y puede meter a alguien
   * en la ronda siguiente, que es otro partido de esta misma lista.
   */
  async function guardar(
    entrada: WallEntry,
    score: MatchScore,
    noShowEntryId: string | null,
  ): Promise<void> {
    await api.saveMatchScore(entrada.tournamentId, entrada.match.id, score, noShowEntryId);
    setAnotando(null);
    await load();
  }

  async function comentar(entrada: WallEntry, body: string): Promise<void> {
    try {
      await api.addComment(entrada.match.id, body);
      await load();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  async function borrarComentario(comentario: MatchComment): Promise<void> {
    try {
      await api.removeComment(comentario.id);
      await load();
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  /**
   * Las reacciones contestan la cuenta nueva y se aplica sola: volver a pedir
   * la lista entera por un 🔥 hace que el botón tarde medio segundo en
   * prenderse, y nadie lo aprieta dos veces.
   */
  async function reaccionar(entrada: WallEntry, emoji: string, on: boolean): Promise<void> {
    try {
      const cuenta = await api.setReaction(entrada.match.id, emoji, on);
      setProximos((lista) =>
        lista === null
          ? null
          : lista.map((x) => (x.match.id === entrada.match.id ? { ...x, reactions: cuenta } : x)),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    }
  }

  return (
    <>
      {error !== null && <Alert kind="error">{error}</Alert>}

      {proximos === null ? (
        <p className="help">{t('common.loading')}</p>
      ) : proximos.length === 0 ? (
        <p className="help">{t('wall.emptyUpcoming')}</p>
      ) : (
        <>
          {pagina.visibles.map((entrada) => (
            <WallCard
              key={entrada.match.id}
              entrada={entrada}
              yoId={session?.user.id ?? ''}
              onScore={setAnotando}
              onComment={comentar}
              onDeleteComment={borrarComentario}
              onReact={reaccionar}
              editor={
                anotando?.match.id === entrada.match.id ? (
                  <ScoreEditor
                    {...sidesOfMatch(anotando.match)}
                    onSave={(score, noShow) => guardar(anotando, score, noShow)}
                    onCancel={() => setAnotando(null)}
                  />
                ) : null
              }
            />
          ))}
          <Paginador pagina={pagina.pagina} paginas={pagina.paginas} irA={pagina.irA} />
        </>
      )}
    </>
  );
}
